import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BlockHeroCompact,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionTitleClass,
  blockLedeClass,
  blockTechnicalLabelClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";
import ENTRIES from "./entries";

/** "2026-05-20" → "20 May 2026" */
function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * /Writing — index page.
 *
 * Vertical list of entries. Each row shows: title (clickable text) → date +
 * tags mono strip → 1–2 line summary. Clicking the text opens the full
 * entry at /Writing/:slug. The whitespace around the text is intentionally
 * not part of the click target.
 */
export default function WritingIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="Writing"
          description="Short notes on computational design, the tools I am building, and the things I learn while building them. Newest first."
          meta={["Notes", "Newest first"]}
        />

        {ENTRIES.length === 0 ? (
          <p className={blockItalicSecondaryNoteClass}>No entries yet.</p>
        ) : (
          <ul className="mt-8 lg:mt-10 border-t border-border dark:border-neutral-800 list-none p-0 m-0">
            {ENTRIES.map(({ meta }) => {
              const to = `/Writing/${meta.slug}`;
              return (
                <li
                  key={meta.slug}
                  className="border-b border-border dark:border-neutral-800 py-6 lg:py-8"
                >
                  {/* Title — inline Link so only the text characters are
                      clickable, not the surrounding whitespace. */}
                  <h2 className={`${blockSectionTitleClass} mb-2`}>
                    <Link
                      to={to}
                      data-cursor="pointer"
                      className="hover:opacity-70 transition-opacity"
                    >
                      {meta.title}
                    </Link>
                  </h2>

                  {/* Date + tags — clickable text only. */}
                  <p className={`${blockTechnicalLabelClass} mb-3`}>
                    <Link
                      to={to}
                      data-cursor="pointer"
                      className="hover:text-black dark:hover:text-white transition-colors"
                    >
                      {formatDate(meta.date)}
                      {Array.isArray(meta.tags) && meta.tags.length > 0 && (
                        <>
                          <span aria-hidden className="mx-2 text-content-subtle dark:text-neutral-600">·</span>
                          {meta.tags.join(" · ")}
                        </>
                      )}
                    </Link>
                  </p>

                  {/* Summary — clickable text only. The Link is inline so
                      the hit area follows the text glyphs, not the block. */}
                  <p className={`${blockLedeClass} m-0`}>
                    <Link
                      to={to}
                      data-cursor="pointer"
                      className="hover:opacity-70 transition-opacity"
                    >
                      {meta.summary}
                    </Link>
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
